import { css, unsafeCSS } from 'lit'

import { HEADER_HEIGHT_PX } from '../geometry.js'

const headerHeight = unsafeCSS(`${HEADER_HEIGHT_PX}px`)

/**
 * Theme token definitions for <ligonier-header>.
 *
 * The :host block declares the complete set of tokens with their "default"
 * values so every token always has a value regardless of which theme is active.
 *
 * Each named theme only overrides the tokens that visually differ from the
 * default, keeping theme blocks minimal and additions cheap.
 */
export const themeStyles = css`
  /* ── Default token values (white header, no border) ─────────────────── */
  :host {
    /* Header bar */
    --header-height: ${headerHeight};
    --header-bg: #ffffff;
    --header-border-bottom: none;
    --header-shadow: none;
    --header-transition: none;

    /* Nav links (center bar, desktop) */
    --nav-link-color: #768d2f;

    /* Search input */
    --search-input-border-color: #100b00;
    --search-input-focus-border-color: #768d2f;
    --search-input-bg: #ffffff;
    --search-input-text-color: #333333;
    --search-input-placeholder-color: #949494;
    --search-dropdown-bg: #ffffff;
    --search-dropdown-border-color: #e5e5e5;
    --search-dropdown-hover-bg: #f5f7ee;
    --search-spinner-color: #768d2f;
    --search-spinner-track-color: #e5e5e5;

    /* Mobile search bar (below header on small screens) */
    --mobile-search-bg: #ffffff;
    --mobile-search-border-color: #e5e5e5;
    --mobile-search-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

    /* Dropdown panel (desktop nav) */
    --dropdown-bg: #ffffff;

    /* Slide-out menu */
    --menu-bg: #ffffff;
    --menu-border-top: 1px solid #e9e9e9;
    --menu-section-border-color: #e9e9e9;
    --menu-section-title-color: #1f1b16;
    --menu-item-color: #3f3a35;
    --menu-item-hover-color: #768d2f;
    --menu-accent-color: #768d2f;
    --menu-accent-border-color: #768d2f;
    --menu-divider-color: #e5e1d8;
    --menu-desktop-section-title-color: #1f1b16;
    --menu-desktop-link-color: #4f4a45;
    --menu-desktop-link-hover-color: #768d2f;
    --menu-desktop-meta-color: #768d2f;
    --menu-promo-color: #9c9893;
    --menu-overlay-bg: rgba(16, 11, 0, 0.18);
  }

  /* ── cream: warm off-white background with a subtle bottom border ────── */
  :host([theme='cream']) {
    --header-bg: #faf7f2;
    --header-border-bottom: 1px solid rgba(16, 11, 0, 0.08);
    --mobile-search-bg: #faf7f2;
    --dropdown-bg: #faf7f2;
    --menu-bg: #faf7f2;
  }

  /* ── clear: transparent, transitions to white on scroll or menu open ─── */
  :host([theme='clear']) {
    --header-bg: transparent;
    --header-border-bottom: none;
    --header-transition: background-color 0.25s ease-out, box-shadow 0.25s ease-out;
    --nav-link-color: #ffffff;
    --search-input-border-color: rgba(255, 255, 255, 0.6);
    --search-input-focus-border-color: #ffffff;
    --search-input-bg: rgba(255, 255, 255, 0.15);
    --search-input-text-color: #ffffff;
    --search-input-placeholder-color: rgba(255, 255, 255, 0.6);
    --mobile-search-bg: transparent;
    --mobile-search-border-color: transparent;
    --mobile-search-shadow: none;
  }

  :host([theme='clear'][data-scrolled]),
  :host([theme='clear'][data-menu-open]) {
    --header-bg: #ffffff;
    --header-shadow: inset 0 -1px 0 rgba(16, 11, 0, 0.08);
    --nav-link-color: #768d2f;
    --search-input-border-color: #100b00;
    --search-input-focus-border-color: #768d2f;
    --search-input-bg: #ffffff;
    --search-input-text-color: #333333;
    --search-input-placeholder-color: #949494;
    --search-spinner-track-color: #e5e5e5;
    --mobile-search-bg: #ffffff;
    --mobile-search-border-color: #e5e5e5;
    --mobile-search-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  /* ── clear + search engaged: the input itself becomes a solid white surface
     so typing stays readable, while the bar band around it stays transparent
     over the hero (the whole bar still goes white on scroll/menu-open via the
     block above). Scoped to the bar's subtree — the header row's clear-theme
     button styles are hardcoded to revert only on scroll/menu-open, so a
     host-level rule would strand them white-on-white. */
  :host([theme='clear'][data-search-open]) .mobile-search-container {
    --search-input-border-color: #100b00;
    --search-input-focus-border-color: #768d2f;
    --search-input-bg: #ffffff;
    --search-input-text-color: #333333;
    --search-input-placeholder-color: #949494;
    --search-spinner-track-color: #e5e5e5;
  }
`
